<template>
  <div
    v-if="showBanner"
    class="position-fixed top-0 start-0 end-0 pt-3 country-notice-wrapper"
    role="status"
  >
    <MDBCard class="country-notice-card border-0 shadow-sm mx-auto">
      <MDBCardBody class="p-3 position-relative">
        <button
          type="button"
          class="btn-close country-notice-close"
          :aria-label="$t('publicCountryNotice.dismissAria')"
          @click="dismiss"
        />
        <MDBRow class="align-items-center g-3">
          <MDBCol col="12" lg="7" class="text-start">
            <div class="d-flex flex-wrap align-items-center gap-2 mb-2">
              <span class="badge bg-danger text-white text-uppercase fw-semibold px-3 py-2">
                {{ $t("publicCountryNotice.title") }}
              </span>
              <div class="d-inline-flex align-items-center gap-2 text-dark">
                <img
                  v-if="currentTenantFlagSrc"
                  :src="currentTenantFlagSrc"
                  :alt="$t('publicCountryNotice.flagAlt', { code: currentTenant })"
                  class="rounded-1 border country-notice-flag"
                >
                <span class="fw-semibold">
                  {{
                    $t("publicCountryNotice.currentTenant", {
                      name: currentTenantDisplayName,
                      code: currentTenant,
                    })
                  }}
                </span>
              </div>
            </div>

            <template v-if="showVisitStylePrompt && effectiveGeoInfo && switchTargetInfo">
              <p class="mb-1 fw-bold text-dark">
                {{
                  $t("publicCountryNotice.visitingFrom", {
                    name: effectiveGeoDisplayName,
                  })
                }}
              </p>
              <p class="mb-0 small text-muted">
                {{
                  $t("publicCountryNotice.suggestPlatform", {
                    name: switchTargetDisplayName,
                  })
                }}
              </p>
            </template>

            <div
              v-else-if="switchTargetInfo"
              class="d-flex flex-wrap align-items-center gap-2 small text-muted"
            >
              <img
                v-if="getTenantFlagSrc(switchTargetInfo)"
                :src="getTenantFlagSrc(switchTargetInfo)"
                :alt="
                  $t('publicCountryNotice.flagAlt', {
                    code: switchTargetInfo.code,
                  })
                "
                class="rounded-1 border country-notice-flag country-notice-flag--sm"
              >
              <span>
                {{
                  $t("publicCountryNotice.lookingFor", {
                    name: switchTargetDisplayName,
                    code: switchTargetInfo.code,
                  })
                }}
              </span>
            </div>
          </MDBCol>

          <MDBCol
            col="12"
            lg="5"
            class="d-flex flex-column flex-md-row align-items-md-center justify-content-lg-end gap-2"
          >
            <div class="d-flex flex-wrap align-items-center gap-2 justify-content-md-end">
              <MDBBtn
                v-if="switchTargetInfo"
                type="button"
                color="danger"
                size="sm"
                class="d-inline-flex align-items-center justify-content-center gap-2 text-nowrap"
                @click="switchTenant"
              >
                <img
                  v-if="getTenantFlagSrc(switchTargetInfo)"
                  :src="getTenantFlagSrc(switchTargetInfo)"
                  :alt="
                    $t('publicCountryNotice.flagAlt', {
                      code: switchTargetInfo.code,
                    })
                  "
                  class="rounded-1 border country-notice-flag country-notice-flag--sm"
                >
                {{
                  $t("publicCountryNotice.switchTo", {
                    code: switchTargetInfo.code,
                  })
                }}
              </MDBBtn>
            </div>
          </MDBCol>
        </MDBRow>
      </MDBCardBody>
    </MDBCard>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import { useI18n } from "vue-i18n";
import { MDBBtn, MDBCard, MDBCardBody, MDBCol, MDBRow } from "mdb-vue-ui-kit";
import {
  TENANT_PUBLIC_INFO,
  TENANT_SWITCH_TARGETS,
} from "@/configs/publicTenantConfig";
import { buildTenantUrl } from "@/utils/publicSeo";
import { trackPublicCountryEvent } from "@/utils/publicCountryTracking";
import {
  getPublicCountryNoticeContext,
  isCountryNoticeTenant,
} from "@/utils/publicCountryNotice";

const store = useStore();
const route = useRoute();
const { t, te } = useI18n();
const isDismissed = ref(false);
const wasTrackedAsShown = ref(false);

function localizedTenantCountryName(tenantInfo) {
  if (!tenantInfo?.code) {
    return "";
  }
  const key = `account.countries.${tenantInfo.code.toLowerCase()}`;
  if (te(key)) {
    return t(key);
  }
  return tenantInfo.name;
}

const currentTenant = computed(() => store.getters.tenantId);
const currentTenantInfo = computed(() => TENANT_PUBLIC_INFO[currentTenant.value] || null);
const currentTenantDisplayName = computed(() =>
  localizedTenantCountryName(currentTenantInfo.value) || currentTenant.value
);
const currentTenantFlagSrc = computed(() => getTenantFlagSrc(currentTenantInfo.value));

const switchTargetTenant = computed(
  () => TENANT_SWITCH_TARGETS[currentTenant.value] || null
);
const switchTargetInfo = computed(
  () => TENANT_PUBLIC_INFO[switchTargetTenant.value] || null
);

const isCountryNoticeTenantEnabled = computed(() =>
  isCountryNoticeTenant(currentTenant.value)
);

const showBanner = computed(
  () =>
    isCountryNoticeTenantEnabled.value &&
    Boolean(currentTenantInfo.value) &&
    !isDismissed.value
);

const dismissStorageKey = computed(() => `country_notice_dismissed_${currentTenant.value}`);

const switchUrl = computed(() => {
  if (!switchTargetTenant.value) {
    return null;
  }

  const path = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  return buildTenantUrl(switchTargetTenant.value, path);
});

const flagAssets = require.context("@/assets/img/flags", false, /\.svg$/);

function getTenantFlagSrc(tenantInfo) {
  if (!tenantInfo) {
    return null;
  }

  const flagCode = (tenantInfo.flagCode || tenantInfo.code || "").toLowerCase();
  if (!flagCode) {
    return null;
  }

  try {
    return flagAssets(`./${flagCode}.svg`);
  } catch (err) {
    return null;
  }
}

const noticeContext = computed(() =>
  getPublicCountryNoticeContext(currentTenant.value, route.query.forceLocaleHint)
);
const browserSuggestedTenant = computed(
  () => noticeContext.value.browserSuggestedTenant
);
const geoSuggestionInfo = computed(
  () => TENANT_PUBLIC_INFO[browserSuggestedTenant.value] || null
);
const showLocaleMismatchHint = computed(() => noticeContext.value.hasMismatch);
const forcedVisitTenantCode = computed(
  () => noticeContext.value.forcedVisitTenantCode
);
const suggestionDetectionKind = computed(() => noticeContext.value.detection);

const effectiveGeoInfo = computed(() => {
  if (forcedVisitTenantCode.value) {
    return TENANT_PUBLIC_INFO[forcedVisitTenantCode.value] || null;
  }
  if (geoSuggestionInfo.value) {
    return geoSuggestionInfo.value;
  }
  return null;
});
const showVisitStylePrompt = computed(() => showLocaleMismatchHint.value);

const effectiveGeoDisplayName = computed(() => {
  const info = effectiveGeoInfo.value;
  if (!info) {
    return "";
  }
  return localizedTenantCountryName(info) || info.name;
});
const switchTargetDisplayName = computed(() => {
  const info = switchTargetInfo.value;
  if (!info) {
    return "";
  }
  return localizedTenantCountryName(info) || info.name;
});

function trackShown() {
  if (wasTrackedAsShown.value || !showBanner.value) {
    return;
  }

  wasTrackedAsShown.value = true;
  trackPublicCountryEvent("country_switch_banner_shown", {
    from_tenant: currentTenant.value,
    suggested_tenant: switchTargetTenant.value,
    inferred_country: noticeContext.value.inferredCountry,
    detection: suggestionDetectionKind.value,
    path: window.location.pathname,
  });
}

function switchTenant() {
  if (!switchUrl.value || !switchTargetTenant.value) {
    return;
  }

  trackPublicCountryEvent("country_switch_clicked", {
    from_tenant: currentTenant.value,
    to_tenant: switchTargetTenant.value,
    inferred_country: noticeContext.value.inferredCountry,
    detection: suggestionDetectionKind.value,
    source: "public_country_notice",
    locale_mismatch_hint_visible: showVisitStylePrompt.value,
    path: window.location.pathname,
  });

  window.location.href = switchUrl.value;
}

function dismiss() {
  isDismissed.value = true;
  if (dismissStorageKey.value) {
    localStorage.setItem(dismissStorageKey.value, "1");
  }
}

watch(
  () => currentTenant.value,
  () => {
    wasTrackedAsShown.value = false;
    if (!dismissStorageKey.value) {
      isDismissed.value = false;
      return;
    }
    isDismissed.value = localStorage.getItem(dismissStorageKey.value) === "1";
    trackShown();
  },
  { immediate: true }
);

watch(showBanner, () => {
  trackShown();
});

onMounted(() => {
  trackShown();
});
</script>

<style lang="scss" scoped>
.country-notice-wrapper {
  z-index: 1030;
  pointer-events: none;
}

.country-notice-card {
  max-width: 760px;
  width: calc(100% - 2rem);
  border-left: 4px solid #ed1c24;
  pointer-events: auto;
}

.country-notice-close {
  position: absolute;
  top: 1rem;
  right: 1rem;
}

.country-notice-flag {
  width: 18px;
  height: 12px;
  object-fit: cover;
  flex-shrink: 0;
}

.country-notice-flag--sm {
  width: 16px;
  height: 11px;
}
</style>

